import React from "react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export const CHART_COLORS = [
  "#293681",
  "#4a90a4",
  "#95ccdd",
  "#e67e22",
  "#27ae60",
  "#c0392b",
  "#8e44ad",
  "#16a085",
];

export const CHART_HEIGHT = 280;

const GRID_STROKE = "var(--app-border)";
const AXIS_STROKE = "var(--app-border-strong)";
const TICK_FILL = "var(--app-text-muted)";
const CHART_SURFACE = "var(--app-surface-muted)";

export function chartFormatNumber(value, digits = 0) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("ar-EG", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  });
}

export function chartFormatPercent(value) {
  if (value == null || Number.isNaN(Number(value))) return "—";
  return `${chartFormatNumber(value, 1)}%`;
}

const defaultFormatter = (v) => chartFormatNumber(v);

function ChartTooltip({ active, payload, label, valueFormatter = defaultFormatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="reports-chart-tooltip">
      {label ? <div className="reports-chart-tooltip-label">{label}</div> : null}
      {payload.map((entry) => (
        <div key={entry.dataKey} className="reports-chart-tooltip-row">
          <span
            className="reports-chart-tooltip-dot"
            style={{ background: entry.color }}
          />
          <span className="reports-chart-tooltip-name">{entry.name || entry.dataKey}</span>
          <span className="reports-chart-tooltip-value">
            {valueFormatter(entry.value, entry)}
          </span>
        </div>
      ))}
    </div>
  );
}

function ChartLegend({ payload }) {
  if (!payload?.length) return null;
  return (
    <div className="reports-chart-legend">
      {payload.map((entry) => (
        <span key={entry.value} className="reports-chart-legend-item">
          <span
            className="reports-chart-legend-dot"
            style={{ background: entry.color }}
          />
          {entry.value}
        </span>
      ))}
    </div>
  );
}

export function ChartEmptyState({ message = "لا توجد بيانات للعرض" }) {
  return (
    <div className="reports-chart-empty">
      <svg
        className="reports-chart-empty-icon"
        viewBox="0 0 48 48"
        fill="none"
        aria-hidden="true"
      >
        <rect x="4" y="28" width="8" height="16" rx="2" fill="#d5e8ef" />
        <rect x="16" y="18" width="8" height="26" rx="2" fill="#95ccdd" />
        <rect x="28" y="24" width="8" height="20" rx="2" fill="#d5e8ef" />
        <rect x="40" y="12" width="4" height="32" rx="2" fill="#d5e8ef" />
      </svg>
      <span>{message}</span>
    </div>
  );
}

function GradientBar({ id, color }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={color} stopOpacity={0.95} />
        <stop offset="100%" stopColor={color} stopOpacity={0.65} />
      </linearGradient>
    </defs>
  );
}

const axisTick = { fill: TICK_FILL, fontSize: 12 };
const axisLine = { stroke: AXIS_STROKE };

function hasLineChartData(data) {
  return Array.isArray(data) && data.length > 0;
}

function hasBarChartData(data, keys) {
  if (!data?.length) return false;
  return data.some((row) =>
    keys.some((k) => {
      const v = row[k];
      return v != null && Number(v) !== 0;
    }),
  );
}

function piePercentLabel({ name, percent }) {
  if (percent < 0.05) return null;
  return `${name} (${chartFormatPercent(percent * 100)})`;
}

export function ReportBarChart({
  data = [],
  bars = [{ dataKey: "value", name: "", color: CHART_COLORS[0] }],
  xKey = "name",
  layout = "horizontal",
  height = CHART_HEIGHT,
  valueFormatter,
  emptyMessage,
  margin,
  xAxisProps = {},
  yAxisProps = {},
}) {
  const keys = bars.map((b) => b.dataKey);
  if (!hasBarChartData(data, keys)) {
    return <ChartEmptyState message={emptyMessage} />;
  }

  const isVertical = layout === "vertical";
  const defaultMargin = isVertical
    ? { top: 8, right: 24, left: 8, bottom: 8 }
    : { top: 8, right: 8, left: 0, bottom: 0 };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout={layout}
        margin={{ ...defaultMargin, ...margin }}
        barCategoryGap={isVertical ? "20%" : "28%"}
        barGap={4}
      >
        {bars.map((bar, i) => (
          <GradientBar key={bar.dataKey} id={`bar-grad-${bar.dataKey}-${i}`} color={bar.color} />
        ))}
        <CartesianGrid
          strokeDasharray="4 4"
          stroke={GRID_STROKE}
          vertical={!isVertical}
          horizontal={isVertical || true}
        />
        {isVertical ? (
          <>
            <XAxis type="number" tick={axisTick} axisLine={axisLine} tickLine={false} />
            <YAxis
              type="category"
              dataKey={xKey}
              tick={{ ...axisTick, fontSize: 11 }}
              axisLine={axisLine}
              tickLine={false}
              width={yAxisProps.width ?? 100}
              {...yAxisProps}
            />
          </>
        ) : (
          <>
            <XAxis
              dataKey={xKey}
              tick={axisTick}
              axisLine={axisLine}
              tickLine={false}
              {...xAxisProps}
            />
            <YAxis tick={axisTick} axisLine={axisLine} tickLine={false} {...yAxisProps} />
          </>
        )}
        <Tooltip
          content={<ChartTooltip valueFormatter={valueFormatter} />}
          cursor={{ fill: "var(--app-surface-hover)" }}
        />
        {bars.length > 1 ? <Legend content={<ChartLegend />} /> : null}
        {bars.map((bar, i) => (
          <Bar
            key={bar.dataKey}
            dataKey={bar.dataKey}
            name={bar.name || bar.dataKey}
            fill={`url(#bar-grad-${bar.dataKey}-${i})`}
            radius={
              isVertical
                ? [0, 6, 6, 0]
                : [6, 6, 0, 0]
            }
            maxBarSize={bars.length > 1 ? 36 : 48}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export function ReportPieChart({
  data = [],
  dataKey = "value",
  nameKey = "name",
  height = CHART_HEIGHT,
  emptyMessage,
  showLegend = true,
}) {
  const filtered = data.filter((d) => Number(d[dataKey]) > 0);
  if (!filtered.length) {
    return <ChartEmptyState message={emptyMessage} />;
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie
          data={filtered}
          dataKey={dataKey}
          nameKey={nameKey}
          cx="50%"
          cy="46%"
          innerRadius="52%"
          outerRadius="78%"
          paddingAngle={2}
          label={piePercentLabel}
          labelLine={{ stroke: AXIS_STROKE, strokeWidth: 1 }}
        >
          {filtered.map((_, i) => (
            <Cell
              key={i}
              fill={CHART_COLORS[i % CHART_COLORS.length]}
              stroke={CHART_SURFACE}
              strokeWidth={2}
            />
          ))}
        </Pie>
        <Tooltip content={<ChartTooltip />} />
        {showLegend ? <Legend content={<ChartLegend />} /> : null}
      </PieChart>
    </ResponsiveContainer>
  );
}

export function ReportLineChart({
  data = [],
  lines = [{ dataKey: "value", name: "", color: CHART_COLORS[0] }],
  xKey = "name",
  height = CHART_HEIGHT,
  valueFormatter,
  emptyMessage,
  showArea = false,
  xAxisProps = {},
}) {
  if (!hasLineChartData(data)) {
    return <ChartEmptyState message={emptyMessage} />;
  }

  const mergedXAxisProps = {
    interval: 0,
    minTickGap: 2,
    tick: { ...axisTick, fontSize: 11 },
    ...xAxisProps,
  };

  const Chart = LineChart;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <Chart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
        {lines.map((line, i) => (
          <defs key={line.dataKey}>
            <linearGradient id={`line-area-${line.dataKey}-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={line.color} stopOpacity={0.18} />
              <stop offset="100%" stopColor={line.color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
        ))}
        <CartesianGrid strokeDasharray="4 4" stroke={GRID_STROKE} vertical={false} />
        <XAxis
          dataKey={xKey}
          axisLine={axisLine}
          tickLine={false}
          {...mergedXAxisProps}
        />
        <YAxis
          tick={axisTick}
          axisLine={axisLine}
          tickLine={false}
          allowDecimals={false}
          domain={[0, "auto"]}
        />
        <Tooltip
          content={<ChartTooltip valueFormatter={valueFormatter} />}
          cursor={{ stroke: AXIS_STROKE, strokeWidth: 1 }}
        />
        {lines.length > 1 ? <Legend content={<ChartLegend />} /> : null}
        {showArea &&
          lines.map((line, i) => (
            <Area
              key={`area-${line.dataKey}`}
              type="linear"
              dataKey={line.dataKey}
              stroke="none"
              fill={`url(#line-area-${line.dataKey}-${i})`}
              legendType="none"
              connectNulls
            />
          ))}
        {lines.map((line) => (
          <Line
            key={line.dataKey}
            type="linear"
            dataKey={line.dataKey}
            name={line.name || line.dataKey}
            stroke={line.color}
            strokeWidth={2.5}
            connectNulls
            dot={{ r: 4, fill: CHART_SURFACE, stroke: line.color, strokeWidth: 2 }}
            activeDot={{ r: 6, fill: line.color, stroke: CHART_SURFACE, strokeWidth: 2 }}
          />
        ))}
      </Chart>
    </ResponsiveContainer>
  );
}
